/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import { type SelectProps } from '@atlaskit/select';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	ConfluenceDatabaseAri,
	ConfluenceEmbedAri,
	ConfluenceFolderAri,
} from '@atlassian/ari/confluence';

import { DebouncedSelector } from '../debounced-selector';

import type {
	ConfluenceContentType,
	ConfluenceEntityResult,
	ConfluencePageOption,
	ConfluencePageSelectorOwnProps,
} from './types';

/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const messages: {
	pageSelectorLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	pageSelectorLabel: {
		/* eslint-disable-next-line @atlassian/i18n/enforce-id-by-package -- legacy onboarding baseline */
		id: 'ai-mate.conversation-assistant-page-selector.page-selector-label',
		defaultMessage: 'Select page',
		description: 'Label for conversation assistant page selector',
	},
});

/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const convertContentType = (result: string): ConfluenceContentType => {
	if (result?.toLowerCase() === 'blogpost') {
		return 'blogPost';
	}
	return result as ConfluenceContentType;
};

/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const extractContentId = (ari: string): string => {
	// Folders, databases, and embeds have a special resourceId format like this: activation/0591fa3a-8f53-4cfe-8f24-2f7be5d4011b/454769345158
	// So we have to use their specific ARI parsers to extract the contentId
	if (ConfluenceFolderAri.check(ari)) {
		return ConfluenceFolderAri.parse(ari).folderId;
	} else if (ConfluenceDatabaseAri.check(ari)) {
		return ConfluenceDatabaseAri.parse(ari).databaseId;
	} else if (ConfluenceEmbedAri.check(ari)) {
		return ConfluenceEmbedAri.parse(ari).embedId;
	} else {
		return AnyAri.parse(ari).resourceId;
	}
};

type BaseConfluencePageSelectorProps = Omit<ConfluencePageSelectorOwnProps, 'cloudId'> & {
	listEntities: (
		query: string,
		types: ConfluenceContentType[],
		spaceId?: string,
	) => Promise<ConfluenceEntityResult[]>;
} & SelectProps<ConfluencePageOption, false>;

/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const BaseConfluencePageSelectorInner = ({
	spaceId,
	contentTypes,
	listEntities,
	...props
}: BaseConfluencePageSelectorProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const loadOptions = useCallback(
		async (searchText: string) => {
			const response = await listEntities(searchText, contentTypes, spaceId);

			return response.flatMap((item) =>
				item.ari && item.type
					? [
							{
								label: item.name,
								value: extractContentId(item.ari),
								type: convertContentType(item.type),
								subType: item.subtype,
							},
						]
					: [],
			);
		},
		[listEntities, spaceId, contentTypes],
	);

	return (
		<DebouncedSelector<ConfluencePageOption>
			loadOptions={loadOptions}
			label={formatMessage(messages.pageSelectorLabel)}
			{...props}
		/>
	);
};
